import type { PipelineStep } from '../components/PipelineStrip';

/**
 * Minimal event shape that both TraceEvent and TraceDetailEvent satisfy.
 * Allows these utilities to work with either type.
 */
export interface TraceEventLike {
  event_id: string;
  event_type: string;
  tool_name?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Canonical event-type → pipeline-step mapping
// These are the ACTUAL event types emitted by the backend (src/types/events.ts)
// ---------------------------------------------------------------------------

export const EVENT_TO_STEP: Record<string, string> = {
  TOOL_CALL_RECEIVED: 'auth',
  POLICY_DECIDED: 'policy',
  DLP_SCANNED: 'dlp',        // disambiguated to dlp_args / dlp_out by order
  INCIDENT_RAISED: 'dlp_args',
  BUDGET_CHECKED: 'budget',
  TOOL_EXECUTED: 'execute',
  TOOL_RESULT_RETURNED: 'audit',
  APPROVAL_REQUESTED: 'policy',
  RATE_LIMITED: 'rate',
};

// ---------------------------------------------------------------------------
// Human-readable labels for event types
// ---------------------------------------------------------------------------

export const EVENT_LABELS: Record<string, string> = {
  TOOL_CALL_RECEIVED: 'Request Received',
  POLICY_DECIDED: 'Policy Evaluation',
  DLP_SCANNED: 'Security Scan',
  BUDGET_CHECKED: 'Budget Check',
  TOOL_EXECUTED: 'Tool Execution',
  TOOL_RESULT_RETURNED: 'Result Returned',
  APPROVAL_REQUESTED: 'Approval Requested',
  APPROVAL_APPROVED: 'Approval Granted',
  APPROVAL_DENIED: 'Approval Denied',
  APPROVAL_EXPIRED: 'Approval Expired',
  INCIDENT_RAISED: 'Incident Raised',
  USAGE_REPORTED: 'Usage Reported',
  POLICY_UPDATED: 'Policy Updated',
  POLICY_RESET: 'Policy Reset',
};

// ---------------------------------------------------------------------------
// Step status inference — uses metadata, not event type substrings
// ---------------------------------------------------------------------------

export function inferStepStatus(
  eventType: string,
  metadata?: Record<string, unknown>,
): PipelineStep['status'] {
  const upper = eventType.toUpperCase();

  if (upper === 'POLICY_DECIDED') {
    const decision = ((metadata?.decision as string) || '').toLowerCase();
    if (decision === 'deny') return 'deny';
    if (decision === 'require_approval') return 'approval';
    if (decision === 'transform') return 'transform';
    return 'allow';
  }
  if (upper === 'BUDGET_CHECKED') {
    if (metadata?.would_exceed) return 'deny';
    return 'ok';
  }
  if (upper === 'TOOL_EXECUTED') {
    const status = ((metadata?.status as string) || '').toLowerCase();
    if (status === 'error') return 'error';
    return 'ok';
  }
  if (upper === 'DLP_SCANNED') {
    const detected = metadata?.detected as string[] | undefined;
    if (detected && detected.length > 0) return 'transform';
    return 'ok';
  }
  if (upper === 'INCIDENT_RAISED') return 'deny';
  if (upper === 'APPROVAL_REQUESTED') return 'approval';
  if (upper === 'RATE_LIMITED') return 'deny';

  return 'ok';
}

// ---------------------------------------------------------------------------
// Build pipeline steps from events
// ---------------------------------------------------------------------------

const DEFAULT_STEP_ORDER = [
  'auth', 'rate', 'anomaly', 'policy', 'dlp_args', 'budget', 'execute', 'dlp_out', 'audit',
];

// Severity ranking for step statuses — higher number = worse
const STATUS_SEVERITY: Record<string, number> = {
  skipped: 0,
  ok: 1,
  allow: 1,
  transform: 2,
  approval: 3,
  pending: 3,
  error: 4,
  deny: 5,
};

export function buildStepsFromEvents(events: TraceEventLike[]): PipelineStep[] {
  const stepMap = new Map<string, PipelineStep>();
  for (const name of DEFAULT_STEP_ORDER) {
    stepMap.set(name, { name, status: 'skipped' });
  }

  let dlpCount = 0;
  for (const event of events) {
    const upper = event.event_type.toUpperCase();
    let stepName = EVENT_TO_STEP[upper];

    // DLP_SCANNED appears twice per tool call: args then output.
    // For multi-call tasks, alternate between dlp_args and dlp_out.
    if (upper === 'DLP_SCANNED') {
      dlpCount++;
      stepName = dlpCount % 2 === 1 ? 'dlp_args' : 'dlp_out';
    }

    if (stepName) {
      const status = inferStepStatus(event.event_type, event.metadata);
      const existing = stepMap.get(stepName);
      // Always escalate to the worst status seen for this step
      if (existing && (STATUS_SEVERITY[status] ?? 0) > (STATUS_SEVERITY[existing.status] ?? 0)) {
        stepMap.set(stepName, { name: stepName, status });
      }
    }
  }

  // If we have events but auth is still skipped, assume it passed
  if (events.length > 0 && stepMap.get('auth')?.status === 'skipped') {
    stepMap.set('auth', { name: 'auth', status: 'ok' });
  }

  // Mark steps after a deny/approval as skipped (they didn't run)
  let blocked = false;
  return DEFAULT_STEP_ORDER.map((n) => {
    const step = stepMap.get(n)!;
    if (blocked && step.status === 'skipped') return step;
    if (step.status === 'deny' || step.status === 'approval' || step.status === 'error') {
      blocked = true;
    }
    return step;
  });
}

// ---------------------------------------------------------------------------
// Overall status derivation — checks metadata fields, not substrings
// ---------------------------------------------------------------------------

export function deriveOverallStatus(events: TraceEventLike[]): string {
  for (const e of events) {
    if (e.event_type === 'POLICY_DECIDED') {
      const decision = ((e.metadata?.decision as string) || '').toLowerCase();
      if (decision === 'deny') return 'denied';
      if (decision === 'require_approval') return 'approval';
    }
    if (e.event_type === 'BUDGET_CHECKED' && e.metadata?.would_exceed) return 'budget_exceeded';
    if (e.event_type === 'INCIDENT_RAISED') return 'incident';
    if (e.event_type === 'RATE_LIMITED') return 'rate_limited';
    if (e.event_type === 'TOOL_EXECUTED') {
      const status = ((e.metadata?.status as string) || '').toLowerCase();
      if (status === 'error') return 'error';
    }
  }
  const hasResult = events.some(e => e.event_type === 'TOOL_RESULT_RETURNED');
  if (hasResult) return 'ok';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Format duration — human-readable from raw milliseconds
// ---------------------------------------------------------------------------

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSeconds = Math.floor(ms / 1000);
  if (ms < 3_600_000) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Format tool name — show "GET api.example.com/path" instead of "http_get"
// ---------------------------------------------------------------------------

export function formatToolName(toolName: string, events?: TraceEventLike[]): string {
  // Try to extract method + URL from TOOL_CALL_RECEIVED metadata
  if (events) {
    const received = events.find(e => e.event_type === 'TOOL_CALL_RECEIVED');
    const args = received?.metadata?.args_snapshot as Record<string, unknown> | undefined;
    if (args) {
      const method = ((args.method as string) || '').toUpperCase();
      const url = args.url as string | undefined;
      if (method && url) {
        try {
          const parsed = new URL(url);
          const host = parsed.hostname;
          const pathPart = parsed.pathname === '/' ? '' : parsed.pathname;
          return `${method} ${host}${pathPart}`;
        } catch {
          // URL parsing failed, fall through
        }
      }
    }
  }

  // Fallback: humanize the tool name
  if (toolName.startsWith('http_') || toolName.startsWith('http.')) {
    const method = toolName.replace(/^http[._]/, '').toUpperCase();
    return `HTTP ${method}`;
  }
  return toolName;
}

// ---------------------------------------------------------------------------
// Event colors — metadata-aware
// ---------------------------------------------------------------------------

export function getEventColor(eventType: string, metadata?: Record<string, unknown>): string {
  const upper = eventType.toUpperCase();

  if (upper === 'TOOL_CALL_RECEIVED') return 'var(--color-info)';

  if (upper === 'POLICY_DECIDED') {
    const decision = ((metadata?.decision as string) || '').toLowerCase();
    if (decision === 'deny') return 'var(--color-error)';
    if (decision === 'require_approval') return 'var(--color-warning)';
    if (decision === 'transform') return 'var(--accent-violet)';
    return 'var(--accent-teal)';
  }

  if (upper === 'BUDGET_CHECKED') {
    return metadata?.would_exceed ? 'var(--color-error)' : 'var(--accent-teal)';
  }

  if (upper === 'TOOL_EXECUTED') {
    const status = ((metadata?.status as string) || '').toLowerCase();
    if (status === 'error') return 'var(--color-error)';
    return 'var(--accent-teal)';
  }

  if (upper === 'TOOL_RESULT_RETURNED') return 'var(--accent-teal)';

  if (upper === 'DLP_SCANNED') {
    const detected = metadata?.detected as string[] | undefined;
    if (detected && detected.length > 0) return 'var(--accent-violet)';
    return 'var(--accent-teal)';
  }

  if (upper === 'INCIDENT_RAISED') return 'var(--color-error)';
  if (upper.includes('APPROVAL_REQUESTED')) return 'var(--color-warning)';
  if (upper.includes('APPROVAL_APPROVED')) return 'var(--accent-teal)';
  if (upper.includes('APPROVAL_DENIED') || upper.includes('APPROVAL_EXPIRED')) return 'var(--color-error)';

  return 'var(--text-tertiary)';
}

export function getEventBgColor(eventType: string, metadata?: Record<string, unknown>): string {
  const upper = eventType.toUpperCase();

  if (upper === 'TOOL_CALL_RECEIVED') return 'var(--color-info-muted)';

  if (upper === 'POLICY_DECIDED') {
    const decision = ((metadata?.decision as string) || '').toLowerCase();
    if (decision === 'deny') return 'var(--color-error-muted)';
    if (decision === 'require_approval') return 'var(--color-warning-muted)';
    if (decision === 'transform') return 'var(--color-violet-muted)';
    return 'var(--color-teal-muted)';
  }

  if (upper === 'BUDGET_CHECKED') {
    return metadata?.would_exceed ? 'var(--color-error-muted)' : 'var(--color-teal-muted)';
  }

  if (upper === 'TOOL_EXECUTED') {
    const status = ((metadata?.status as string) || '').toLowerCase();
    if (status === 'error') return 'var(--color-error-muted)';
    return 'var(--color-teal-muted)';
  }

  if (upper === 'TOOL_RESULT_RETURNED') return 'var(--color-teal-muted)';

  if (upper === 'DLP_SCANNED') {
    const detected = metadata?.detected as string[] | undefined;
    if (detected && detected.length > 0) return 'var(--color-violet-muted)';
    return 'var(--color-teal-muted)';
  }

  if (upper === 'INCIDENT_RAISED') return 'var(--color-error-muted)';
  if (upper.includes('APPROVAL_REQUESTED')) return 'var(--color-warning-muted)';
  if (upper.includes('APPROVAL_APPROVED')) return 'var(--color-teal-muted)';
  if (upper.includes('APPROVAL_DENIED') || upper.includes('APPROVAL_EXPIRED')) return 'var(--color-error-muted)';

  return 'rgba(90,100,120,0.12)';
}

// ---------------------------------------------------------------------------
// Structured event summary — key-value pairs instead of raw JSON
// ---------------------------------------------------------------------------

export interface EventSummaryEntry {
  label: string;
  value: string;
  color?: string;
}

export function getEventSummary(eventType: string, metadata?: Record<string, unknown>): EventSummaryEntry[] {
  if (!metadata) return [];
  const entries: EventSummaryEntry[] = [];

  switch (eventType) {
    case 'TOOL_CALL_RECEIVED': {
      if (metadata.capability) entries.push({ label: 'Capability', value: String(metadata.capability) });
      if (metadata.platform) entries.push({ label: 'Platform', value: String(metadata.platform) });
      if (metadata.has_constraints) entries.push({ label: 'Constraints', value: 'Yes' });
      const labels = metadata.labels as string[] | undefined;
      if (labels && labels.length > 0) entries.push({ label: 'Labels', value: labels.join(', ') });
      const args = metadata.args_snapshot as Record<string, unknown> | undefined;
      if (args) {
        if (args.method) entries.push({ label: 'Method', value: String(args.method).toUpperCase() });
        if (args.url) entries.push({ label: 'URL', value: String(args.url) });
      }
      break;
    }
    case 'POLICY_DECIDED': {
      const decision = ((metadata.decision as string) || '').toLowerCase();
      const decisionColor = decision === 'deny' ? 'var(--color-error)'
        : decision === 'require_approval' ? 'var(--color-warning)'
        : decision === 'transform' ? 'var(--accent-violet)'
        : 'var(--accent-teal)';
      entries.push({ label: 'Decision', value: String(metadata.decision || 'unknown'), color: decisionColor });
      if (metadata.rule_id) entries.push({ label: 'Rule', value: String(metadata.rule_id) });
      const reasons = metadata.reasons as string[] | undefined;
      if (reasons && reasons.length > 0) entries.push({ label: 'Reasons', value: reasons.join('; ') });
      break;
    }
    case 'DLP_SCANNED': {
      const detected = metadata.detected as string[] | undefined;
      if (metadata.actor_display) entries.push({ label: 'Actor', value: String(metadata.actor_display) });
      if (metadata.target_url) entries.push({ label: 'URL', value: String(metadata.target_url) });
      entries.push({ label: 'Findings', value: String(metadata.findings_count ?? (detected?.length ?? 0)) });
      if (detected && detected.length > 0) {
        entries.push({ label: 'Types', value: detected.join(', '), color: 'var(--accent-violet)' });
      }
      if (metadata.severity) entries.push({ label: 'Severity', value: String(metadata.severity) });
      if (metadata.redaction_count) entries.push({ label: 'Redactions', value: String(metadata.redaction_count) });
      const redactions = metadata.redactions as Array<{ path?: string; type?: string; method?: string; masked_preview?: string }> | undefined;
      if (redactions && redactions.length > 0) {
        for (const r of redactions) {
          const parts: string[] = [];
          if (r.masked_preview) parts.push(r.masked_preview);
          if (r.path) parts.push(`at ${r.path}`);
          if (r.method) parts.push(`[${r.method}]`);
          entries.push({ label: r.type || 'Match', value: parts.join(' '), color: 'var(--color-warning)' });
        }
      }
      break;
    }
    case 'BUDGET_CHECKED': {
      const wouldExceed = metadata.would_exceed as boolean;
      entries.push({
        label: 'Status',
        value: wouldExceed ? 'Over budget' : 'Within budget',
        color: wouldExceed ? 'var(--color-error)' : 'var(--accent-teal)',
      });
      if (metadata.estimated_cost !== undefined) entries.push({ label: 'Estimated Cost', value: `$${Number(metadata.estimated_cost).toFixed(4)}` });
      if (metadata.spent_to_date !== undefined) entries.push({ label: 'Spent to Date', value: `$${Number(metadata.spent_to_date).toFixed(4)}` });
      if (metadata.remaining !== undefined) entries.push({ label: 'Remaining', value: `$${Number(metadata.remaining).toFixed(4)}` });
      break;
    }
    case 'TOOL_EXECUTED': {
      const status = (metadata.status as string) || 'unknown';
      entries.push({
        label: 'Status',
        value: status,
        color: status === 'error' ? 'var(--color-error)' : 'var(--accent-teal)',
      });
      if (metadata.duration_ms !== undefined) entries.push({ label: 'Duration', value: `${metadata.duration_ms}ms` });
      if (metadata.http_status !== undefined) entries.push({ label: 'HTTP Status', value: String(metadata.http_status) });
      break;
    }
    case 'TOOL_RESULT_RETURNED': {
      if (metadata.status) entries.push({ label: 'Status', value: String(metadata.status) });
      if (metadata.duration_ms !== undefined) entries.push({ label: 'Total Duration', value: `${metadata.duration_ms}ms` });
      const timings = metadata.step_timings as Record<string, number> | undefined;
      if (timings) {
        const parts = Object.entries(timings).map(([k, v]) => `${k}: ${v}ms`).join(', ');
        entries.push({ label: 'Step Timings', value: parts });
      }
      break;
    }
    case 'APPROVAL_REQUESTED': {
      if (metadata.scope) entries.push({ label: 'Scope', value: String(metadata.scope) });
      if (metadata.reason) entries.push({ label: 'Reason', value: String(metadata.reason) });
      if (metadata.ttl_seconds) entries.push({ label: 'TTL', value: `${metadata.ttl_seconds}s` });
      if (metadata.expires_at) entries.push({ label: 'Expires', value: new Date(String(metadata.expires_at)).toLocaleString() });
      break;
    }
    case 'APPROVAL_APPROVED': {
      if (metadata.approver_id) entries.push({ label: 'Approver', value: String(metadata.approver_id) });
      break;
    }
    case 'APPROVAL_DENIED': {
      if (metadata.approver_id) entries.push({ label: 'Approver', value: String(metadata.approver_id) });
      if (metadata.reason) entries.push({ label: 'Reason', value: String(metadata.reason), color: 'var(--color-error)' });
      break;
    }
    case 'INCIDENT_RAISED': {
      if (metadata.severity) entries.push({
        label: 'Severity',
        value: String(metadata.severity),
        color: metadata.severity === 'critical' || metadata.severity === 'high' ? 'var(--color-error)' : 'var(--color-warning)',
      });
      if (metadata.incident_type) entries.push({ label: 'Type', value: String(metadata.incident_type) });
      if (metadata.description) entries.push({ label: 'Description', value: String(metadata.description) });
      if (metadata.recommended_action) entries.push({ label: 'Action', value: String(metadata.recommended_action) });
      break;
    }
    default: {
      // Generic fallback: show all metadata keys
      for (const [key, val] of Object.entries(metadata)) {
        entries.push({ label: key, value: typeof val === 'object' ? JSON.stringify(val) : String(val) });
      }
    }
  }

  return entries;
}

// ---------------------------------------------------------------------------
// Metadata extraction helpers — pull structured data from event arrays
// ---------------------------------------------------------------------------

export function extractHttpStatus(events: TraceEventLike[]): number | undefined {
  for (const e of events) {
    if (e.event_type === 'TOOL_EXECUTED' && e.metadata?.http_status !== undefined) {
      return Number(e.metadata.http_status);
    }
  }
  return undefined;
}

export function extractDlpSummary(events: TraceEventLike[]): { findingsCount: number; redactionCount: number } {
  let findingsCount = 0;
  let redactionCount = 0;
  for (const e of events) {
    if (e.event_type === 'DLP_SCANNED' && e.metadata) {
      const detected = e.metadata.detected as string[] | undefined;
      findingsCount += Number(e.metadata.findings_count ?? (detected?.length ?? 0));
      redactionCount += Number(e.metadata.redaction_count ?? 0);
    }
  }
  return { findingsCount, redactionCount };
}

export function extractCostEstimate(events: TraceEventLike[]): number | undefined {
  for (const e of events) {
    if (e.event_type === 'BUDGET_CHECKED' && e.metadata?.estimated_cost !== undefined) {
      return Number(e.metadata.estimated_cost);
    }
  }
  return undefined;
}

export function extractDenyInfo(events: TraceEventLike[]): { ruleId?: string; reasons: string[] } | undefined {
  for (const e of events) {
    if (e.event_type === 'POLICY_DECIDED') {
      const decision = ((e.metadata?.decision as string) || '').toLowerCase();
      if (decision === 'deny') {
        const reasons = (e.metadata?.reasons as string[]) || [];
        return {
          ruleId: e.metadata?.rule_id as string | undefined,
          reasons,
        };
      }
    }
  }
  return undefined;
}

export function extractIncidentCount(events: TraceEventLike[]): number {
  let count = 0;
  for (const e of events) {
    if (e.event_type === 'INCIDENT_RAISED') count++;
  }
  return count;
}

// ---------------------------------------------------------------------------
// Unified status badge styling
// ---------------------------------------------------------------------------

export function getStatusStyle(status?: string): { bg: string; text: string; label: string } {
  const s = (status ?? '').toLowerCase();
  if (s === 'ok' || s === 'success' || s === 'allowed' || s === 'allow')
    return { bg: 'var(--color-teal-muted)', text: 'var(--accent-teal)', label: 'Allowed' };
  if (s === 'denied' || s === 'deny')
    return { bg: 'var(--color-error-muted)', text: 'var(--color-error)', label: 'Denied' };
  if (s === 'error' || s === 'blocked')
    return { bg: 'var(--color-error-muted)', text: 'var(--color-error)', label: 'Error' };
  if (s === 'rate_limited')
    return { bg: 'var(--color-error-muted)', text: 'var(--color-error)', label: 'Rate Limited' };
  if (s === 'budget_exceeded')
    return { bg: 'var(--color-error-muted)', text: 'var(--color-error)', label: 'Over Budget' };
  if (s === 'approval' || s === 'pending')
    return { bg: 'var(--color-warning-muted)', text: 'var(--color-warning)', label: 'Approval Required' };
  if (s === 'incident')
    return { bg: 'var(--color-error-muted)', text: 'var(--color-error)', label: 'Incident' };
  return { bg: 'var(--color-info-muted)', text: 'var(--text-secondary)', label: status ?? 'Unknown' };
}
